/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../theme/theme"
import { Alert, CircularProgress, css, Snackbar, Typography } from "@mui/material"
import { useState } from "react"
import { mobileCss } from "../../util/isMobile"
import { MyInput } from "../../components/input/MyInput"
import { MyButton } from "../../components/button/MyButton"
import { CheckCircle, Send } from "@mui/icons-material"
import { useTranslation } from "react-i18next"
import { createEmailHtml, sendEmail, validateEmail, validatePhone } from "../../../services/mailService"

const FormContainerStyle = withMyTheme((theme) => css`
    position: relative;
    z-index: 1;
    max-width: 80%;
    margin-left: auto;
    margin-right: auto;
    
    ${mobileCss(`
        max-width: 100%;
    `)}
`)

const FormTitleStyle = withMyTheme((theme) => css`
    font-size: 1.4rem;
    font-weight: 600;
    margin-bottom: .75rem;
    color: ${theme.palette.primary.dark};
    text-align: center;
`)

const ContactInput = withMyTheme((theme) => css`
    width: 100%;
    margin-bottom: 1.5rem;
`)

const SendButtonContainerStyle = withMyTheme((theme) => css`
    display: flex;
    justify-content: center;
`)

const LoadingContainerStyle = withMyTheme((theme) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 1rem;
    color: ${theme.palette.primary.main};
`)

const ErrorTextStyle = withMyTheme((theme) => css`
    color: ${theme.palette.error.main};
    margin-top: 1rem;
    text-align: center;
    font-size: 0.9rem;
`)

const SuccessMessageStyle = withMyTheme((theme) => css`
    text-align: center;
    margin: 2rem 0;
    color: ${theme.palette.primary.main};
    font-size: 1.2rem;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    
    svg {
        font-size: 3rem;
        color: ${theme.palette.success.main};
    }
`)

interface ContactFormProps {
    className?: string;
}

// Validation functions are now imported from mailService.ts

export const ContactForm = ({ className }: ContactFormProps) => {
    const { t } = useTranslation();
    const [message, setMessage] = useState('');
    const [contact, setContact] = useState('');
    const [contactError, setContactError] = useState<string | false>(false);
    const [messageError, setMessageError] = useState<string | false>(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    // Validate contact field
    const validateContactField = () => {
        if (!contact) {
            setContactError(t('contact.validation.contactRequired'));
            return false;
        }
        
        // Check if it's an email or phone number
        if (!validateEmail(contact) && !validatePhone(contact)) {
            setContactError(t('contact.validation.invalidContact'));
            return false;
        }
        
        setContactError(false);
        return true;
    };

    // Validate message field
    const validateMessageField = () => {
        if (!message) {
            setMessageError(t('contact.validation.messageRequired'));
            return false;
        }
        
        if (message.length < 10) {
            setMessageError(t('contact.validation.messageTooShort'));
            return false;
        }
        
        setMessageError(false);
        return true;
    };

    // Handle form submission
    const handleSubmit = async () => {
        // Reset previous errors
        setSubmitError(null);
        
        // Validate fields
        const isContactValid = validateContactField();
        const isMessageValid = validateMessageField();
        
        if (!isContactValid || !isMessageValid) {
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            // Create HTML email content
            const isEmail = validateEmail(contact);
            const emailFrom = isEmail ? contact : 'the.weddie.site@gmail.com';
            
            // Generate HTML content using the service function
            const htmlContent = createEmailHtml(contact, message);
            
            // Prepare request data
            const requestData = {
                sentTo: 'milszym@gmail.com',
                sentFrom: emailFrom,
                topic: 'New Contact Form Submission - Weddie Site',
                message,
                html: htmlContent
            };
            
            // Send email using the service function
            const response = await sendEmail(requestData);
            
            if (!response.ok) {
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
            
            // Handle success
            setSubmitSuccess(true);
            setFormSubmitted(true);
            setContact('');
            setMessage('');
            
            // Reset success message after 5 seconds
            setTimeout(() => {
                setSubmitSuccess(false);
            }, 5000);
            
        } catch (error) {
            console.error('Error sending email:', error);
            setSubmitError(t('contact.validation.submitError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div css={FormContainerStyle} className={className}>
            <Typography css={FormTitleStyle}>
                {t('contact.formTitle')}
            </Typography>
            
            {formSubmitted ? (
                <div css={SuccessMessageStyle}>
                    <CheckCircle />
                    <Typography>
                        {t('contact.validation.submitSuccess')}
                    </Typography>
                </div>
            ) : (
                <>
                    <MyInput
                        value={contact}
                        label={t('contact.formContactLabel')}
                        placeholder={t('contact.formContactPlaceholder')}
                        additionalCss={ContactInput}
                        onChange={(value) => {
                            setContact(value);
                            if (contactError) validateContactField();
                        }}
                        onBlur={validateContactField}
                        error={!!contactError}
                        helperText={contactError}
                    />
                    
                    <MyInput
                        value={message}
                        label={t('contact.formMessageLabel')}
                        placeholder={t('contact.formMessagePlaceholder')}
                        multiline={true}
                        onChange={(value) => {
                            setMessage(value);
                            if (messageError) validateMessageField();
                        }}
                        onBlur={validateMessageField}
                        additionalCss={ContactInput}
                        rows={3}
                        error={!!messageError}
                        helperText={messageError}
                    />
                    
                    {submitError && (
                        <Typography css={ErrorTextStyle}>
                            {submitError}
                        </Typography>
                    )}
                    
                    <div css={SendButtonContainerStyle}>
                        {isSubmitting ? (
                            <div css={LoadingContainerStyle}>
                                <CircularProgress size={24} />
                            </div>
                        ) : (
                            <MyButton 
                                text={t('contact.formSendButton')} 
                                variant="primary" 
                                endIcon={<Send />}
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            />
                        )}
                    </div>
                </>
            )}
            
            <Snackbar
                open={submitSuccess}
                autoHideDuration={5000}
                onClose={() => setSubmitSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" variant="filled">
                    {t('contact.validation.submitSuccess')}
                </Alert>
            </Snackbar>
        </div>
    );
};
